import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'app/redux/store';
import { questionnairesApi } from 'app/services/questionnaires';
import { resetQuestionnaire } from '../../actions';
import lodashSet from 'lodash.set';

export interface GeneralInfoStateValues {
  businessName: Nullable<string>;
  employerIdentificationNumber: Nullable<string>;
  businessAddress: Nullable<string>;
  city: Nullable<string>;
  zipCode: Nullable<string>;
  state: Nullable<string>;
  dateBusinessWasFormed: Nullable<string>;
  thisEntityIsOwnedByAn: Nullable<string>;
  inWhatStateDidYouRegisterYourBusiness: Nullable<string>;
  isYourBusinessRegisteredInMultipleStates: Nullable<TYesOrNo>;
  addAnotherState: addAnotherState;
  californiaSosNumber: Nullable<string>;
  didYouMakeYourYearCaliforniaFranchiseTaxPayment: Nullable<TYesOrNo>;
  pleaseUploadACopyOfYourProofOfFranchiseTaxPayment: Nullable<string>;
  willThisBeAFinalYearsTaxReturnForABusinessYouAreDissolving: Nullable<TYesOrNo>;
  isThisAZeroActivityReturn: Nullable<TYesOrNo>;
  isThisYourFirstYearFilingATaxReturnForBusiness: Nullable<TYesOrNo>;
  pleaseUploadACopyOfYourPreviousYearsTaxReturn: Nullable<string>;
  wereYouRequiredToPayAFranchiseTaxOrAnnualFilingFeeThisTaxYear: Nullable<TYesOrNo>;
  howMuchDidYouPay: Nullable<string>;
  hasYourBusinessReceivedAK1FromAnotherSCorpOrPartnership: Nullable<TYesOrNo>;
}

export interface GeneralInfoState {
  values: GeneralInfoStateValues;
}
type addAnotherState = addAnotherStateRow[];
export type addAnotherStateRow = {
  state: Nullable<string>;
};

const initialState: GeneralInfoState = {
  values: {
    businessName: null,
    employerIdentificationNumber: null,
    businessAddress: null,
    city: null,
    zipCode: null,
    state: null,
    dateBusinessWasFormed: null,
    thisEntityIsOwnedByAn: null,
    inWhatStateDidYouRegisterYourBusiness: null,
    isYourBusinessRegisteredInMultipleStates: null,
    addAnotherState: [],
    californiaSosNumber: null,
    didYouMakeYourYearCaliforniaFranchiseTaxPayment: null,
    pleaseUploadACopyOfYourProofOfFranchiseTaxPayment: null,
    willThisBeAFinalYearsTaxReturnForABusinessYouAreDissolving: null,
    isThisAZeroActivityReturn: null,
    isThisYourFirstYearFilingATaxReturnForBusiness: null,
    pleaseUploadACopyOfYourPreviousYearsTaxReturn: null,
    wereYouRequiredToPayAFranchiseTaxOrAnnualFilingFeeThisTaxYear: null,
    howMuchDidYouPay: null,
    hasYourBusinessReceivedAK1FromAnotherSCorpOrPartnership: null,
  },
};

const GeneralInfoSlice = createSlice({
  name: 'GeneralInfo',
  initialState,
  reducers: {
    reset(state) {
      state = initialState;
    },
    update(state, action: PayloadAction<GeneralInfoStateValues>) {
      state.values = action.payload;
    },
    updateKey(
      state,
      action: PayloadAction<{
        key: keyof GeneralInfoStateValues;
        value: any;
      }>,
    ) {
      lodashSet(state, `values.${action.payload.key}`, action.payload.value);
    },

    updateFieldArrayKey(
      state,
      action: PayloadAction<{
        key: keyof GeneralInfoStateValues;
        value: any;
      }>,
    ) {
      const fieldArray =
        action.payload.value &&
        action.payload.value.map(({ ...field }) => ({
          ...field,
        }));
      if (action.payload.key === 'addAnotherState') {
        state.values[action.payload.key] = fieldArray;
      } else {
        lodashSet(state, `values.${action.payload.key}`, fieldArray);
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(resetQuestionnaire, () => initialState);

    builder.addMatcher(
      questionnairesApi.endpoints.getQuestionnaireById.matchFulfilled,
      (state, { payload }) => {
        state.values = payload.questionnaire.data.taxQuestionnaire
          ? payload.questionnaire.data.taxQuestionnaire?.businessInfo
              ?.generalInfo?.values
          : initialState.values;
      },
    );
  },
});

export const { update, updateKey, updateFieldArrayKey } =
  GeneralInfoSlice.actions;
export default GeneralInfoSlice.reducer;

export const selectGeneralInfo = (state: RootState) =>
  state.taxQuestionnaire.businessInfo.generalInfo;
